"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.READINESS_PART_COUNT = exports.READINESS_ISSUE_MESSAGES = exports.READINESS_ISSUE_CODES = exports.READINESS_STEPS = exports.READINESS_GROUPS = void 0;
exports.readinessGroupOf = readinessGroupOf;
exports.writesToSourceTable = writesToSourceTable;
exports.isSourceRowTable = isSourceRowTable;
exports.updatesSourceRow = updatesSourceRow;
exports.splitsListIntoSourceRow = splitsListIntoSourceRow;
exports.deletesWrittenSourceRows = deletesWrittenSourceRows;
exports.countReadyParts = countReadyParts;
exports.computeAutomationReadiness = computeAutomationReadiness;
const field_types_1 = require("@streamlain/field-types");
const input_1 = require("./input");
const trigger_1 = require("./trigger");
/** The settings panel's tile grid, always in this order. */
exports.READINESS_GROUPS = [
    { id: 'input', tiles: ['source', 'tracking', 'filter', 'rows', 'related'] },
    { id: 'workflow', tiles: ['workflow', 'mapping', 'test', 'failure'] },
    { id: 'output', tiles: ['target', 'fields', 'match', 'fanout'] },
    { id: 'trigger', tiles: ['trigger'] },
    { id: 'history', tiles: ['history'] },
];
exports.READINESS_STEPS = [
    'input',
    'workflow',
    'output',
];
function readinessGroupOf(tile) {
    const group = exports.READINESS_GROUPS.find(entry => entry.tiles.includes(tile));
    if (!group)
        throw new Error(`Unknown readiness tile: ${tile}`);
    return group.id;
}
/** Stable machine code of a gap (UI tests, AI health check, telemetry). */
exports.READINESS_ISSUE_CODES = [
    'input.webhook-missing',
    'input.source-missing',
    'input.source-rowless',
    'input.related-missing',
    'input.related-self',
    'input.status-tracking-required',
    'input.status-tracking-incomplete',
    'input.status-values-missing',
    'input.status-column-shared',
    'input.delete-row-writes-source',
    'input.watched-status-column',
    'workflow.missing',
    'workflow.unpublished',
    'workflow.ai-keys-missing',
    'workflow.safety-blocked',
    'workflow.input-mapping-missing',
    'workflow.relation-mapping-invalid',
    'workflow.options-mapping-invalid',
    'output.target-missing',
    'output.mapping-missing',
    'output.match-key-missing',
    'history.last-run-failed',
];
/**
 * One English sentence per gap for agents. The UI renders `labelKey`
 * through i18n instead; both describe the same gap.
 */
exports.READINESS_ISSUE_MESSAGES = {
    'input.webhook-missing': 'No inbound webhook is bound to the webhook trigger.',
    'input.source-missing': 'No source table is selected; the "on new row" trigger needs one.',
    'input.source-rowless': 'The source table is a mail folder and never receives rows; use the mailbox\'s "Messages" table and filter on its "Folder" column.',
    'input.related-missing': 'Additional inputs require a main table and a relation binding.',
    'input.related-self': 'A related input is the source table itself; a related input must be another table.',
    'input.status-tracking-required': 'An automatic trigger (schedule, new row, changed row) over a source table needs status tracking with a status column.',
    'input.status-tracking-incomplete': 'Status tracking is enabled but no status column is selected.',
    'input.status-values-missing': 'An automatic trigger needs a pending and an error value in status tracking; rows with the error value are not run again automatically.',
    'input.status-column-shared': "Another automatic automation on this source table uses the same status column; its done value would mark this automation's rows as done.",
    'input.delete-row-writes-source': 'Processed rows are deleted although the automation writes to its source table; the written result would be deleted with the row.',
    'input.watched-status-column': 'The "row changed" trigger watches the status tracking column; each run would start the automation again. Remove it from the watched columns or choose another status column.',
    'workflow.missing': 'No workflow is selected.',
    'workflow.unpublished': 'The selected workflow has no published version.',
    'workflow.ai-keys-missing': 'The workflow uses AI providers without a configured workspace API key.',
    'workflow.safety-blocked': 'The topology safety analysis blocks this automation (loop or failed analysis).',
    'workflow.relation-mapping-invalid': 'Map the relation again to a JSON input.',
    'workflow.options-mapping-invalid': 'A workflow field receives the options of a column, but the automation reads no main table (webhook input). Options come from a select column of the main table only.',
    'workflow.input-mapping-missing': 'Required workflow input fields are not mapped to source columns.',
    'output.target-missing': 'An output entry has no table; select one or remove the entry. An automation without output entries writes nothing back.',
    'output.mapping-missing': 'Required workflow output fields are not mapped to target columns.',
    'output.match-key-missing': 'The source table is also a target of an activatable trigger but has no match key; runs would append duplicate rows.',
    'history.last-run-failed': 'The last run failed or was stopped by loop protection.',
};
/** Whether the automation writes to the table its input rows come from. */
function writesToSourceTable(config) {
    return (!!config.sourceTableId &&
        (config.outputTables ?? []).some(table => table.tableId === config.sourceTableId));
}
/**
 * Whether this output table is the table the processed rows come from. A
 * webhook has no source rows.
 */
function isSourceRowTable(config, table) {
    return (config.triggerConfig?.type !== 'webhook' &&
        !!config.sourceTableId &&
        table.tableId === config.sourceTableId);
}
/**
 * Whether this output table writes into the row a run is processing. The
 * mode only counts while the table is the source table.
 */
function updatesSourceRow(config, table) {
    return table.mode === 'update_source' && isSourceRowTable(config, table);
}
/**
 * Updating the processed row cannot be combined with one row per list
 * entry: a run has one processed row, a list has many entries.
 */
function splitsListIntoSourceRow(config) {
    return (config.outputTables ?? []).some(table => !!table.sourceArrayField && updatesSourceRow(config, table));
}
/**
 * Deleting processed rows cannot be combined with writing to the source
 * table: the row a run has just written would be deleted with it. A webhook
 * has no source rows.
 */
function deletesWrittenSourceRows(config) {
    return (config.triggerConfig?.type !== 'webhook' &&
        !!config.statusTracking?.enabled &&
        config.statusTracking.mode === 'delete_row' &&
        writesToSourceTable(config));
}
/** The parts the status counts: the trigger and the three steps. */
exports.READINESS_PART_COUNT = exports.READINESS_STEPS.length + 1;
/** How many of them have nothing open (the status' "2 of 4 ready"). */
function countReadyParts(readiness) {
    const states = [
        readiness.tiles.trigger,
        ...exports.READINESS_STEPS.map(step => readiness.steps[step].state),
    ];
    return states.filter(state => state === 'ok' || state === 'none').length;
}
const LOOP_REASONS = new Set([
    'cycle_blocked',
    'suspected_external_loop',
    'mail_reply_loop',
]);
const STATE_RANK = {
    none: 0,
    ok: 1,
    missing: 2,
    error: 3,
};
function stateOf(issues, fallback) {
    if (issues.some(issue => issue.severity === 'error'))
        return 'error';
    if (issues.some(issue => issue.severity === 'missing'))
        return 'missing';
    return fallback;
}
function computeAutomationReadiness(input) {
    const { config, workflow, schema } = input;
    const trigger = config.triggerConfig?.type ?? 'manual';
    const isWebhook = trigger === 'webhook';
    const hasSource = !!config.sourceTableId && !isWebhook;
    const issues = [];
    const add = (issue) => issues.push(issue);
    // ── Eingabe ──
    const inputConfigured = (0, trigger_1.hasConfiguredAutomationInput)(config);
    if (!inputConfigured) {
        add(isWebhook
            ? {
                code: 'input.webhook-missing',
                tile: 'source',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.execution.missing-webhook-source',
                action: 'select-webhook',
                blocksRun: true,
            }
            : {
                code: 'input.source-missing',
                tile: 'source',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.execution.missing-source',
                action: 'select-source',
                blocksRun: true,
            });
    }
    if (hasSource && input.sourceIsRowless) {
        add({
            code: 'input.source-rowless',
            tile: 'source',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.source-mail-folder',
            action: 'select-source',
            blocksRun: true,
        });
    }
    const secondaries = (config.inputTables ?? []).filter(table => table.role === 'secondary');
    if (secondaries.some(table => !table.tableId) ||
        !(0, input_1.hasOnlyRelatedInputTables)(secondaries) ||
        (!hasSource && secondaries.length > 0)) {
        add({
            code: 'input.related-missing',
            tile: 'related',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.relation-required',
            action: 'select-related',
            blocksRun: true,
        });
    }
    if ((0, input_1.hasSelfRelatedInputTable)(config.sourceTableId, secondaries)) {
        add({
            code: 'input.related-self',
            tile: 'related',
            // A gap to close like a missing relation: it blocks runs and activation.
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.relation-self',
            action: 'select-related',
            blocksRun: true,
        });
    }
    const tracking = config.statusTracking;
    const trackingGap = (0, trigger_1.statusTrackingGap)(config);
    // Tracking is switched on and lacks nothing the trigger needs.
    const trackingComplete = !!tracking?.enabled && trackingGap === null;
    if (hasSource && trackingGap === 'values') {
        add({
            code: 'input.status-values-missing',
            tile: 'tracking',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.tracking-values-missing',
            action: 'select-status-column',
            blocksRun: false,
        });
    }
    else if (hasSource && (trackingGap === 'off' || trackingGap === 'column')) {
        if ((0, trigger_1.needsStatusTracking)(config)) {
            add({
                code: 'input.status-tracking-required',
                tile: 'tracking',
                severity: 'missing',
                labelKey: input.hasStatusColumnCandidate
                    ? tracking?.enabled
                        ? 'com.streamlain.streamlain.tracking.select-column'
                        : 'com.streamlain.streamlain.validation.status-tracking-required'
                    : 'com.streamlain.automation.sidebar.tracking-create-column',
                action: input.hasStatusColumnCandidate
                    ? tracking?.enabled
                        ? 'select-status-column'
                        : 'enable-tracking'
                    : 'create-status-column',
                blocksRun: false,
            });
        }
        else if (tracking?.enabled) {
            add({
                code: 'input.status-tracking-incomplete',
                tile: 'tracking',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.validation.status-tracking-incomplete',
                action: input.hasStatusColumnCandidate
                    ? 'select-status-column'
                    : 'create-status-column',
                blocksRun: true,
            });
        }
    }
    if (hasSource && input.statusColumnShared && (0, trigger_1.needsStatusTracking)(config)) {
        add({
            code: 'input.status-column-shared',
            tile: 'tracking',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.tracking-column-shared',
            action: 'select-status-column',
            blocksRun: false,
        });
    }
    if (trigger === 'row_changed' &&
        (0, trigger_1.watchesStatusColumn)(config.triggerConfig?.watchedColumnIds ?? [], (0, trigger_1.trackedStatusColumnId)(tracking))) {
        add({
            code: 'input.watched-status-column',
            tile: 'tracking',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.trigger.watched-status-column-issue',
            action: 'select-status-column',
            blocksRun: false,
        });
    }
    if (deletesWrittenSourceRows(config)) {
        add({
            code: 'input.delete-row-writes-source',
            tile: 'tracking',
            severity: 'missing',
            labelKey: 'com.streamlain.automation.sidebar.tracking-delete-writes-source',
            action: 'enable-tracking',
            blocksRun: true,
        });
    }
    // ── Workflow ──
    if (!config.workflowId) {
        add({
            code: 'workflow.missing',
            tile: 'workflow',
            severity: 'missing',
            labelKey: 'com.streamlain.streamlain.execution.missing-workflow',
            action: 'select-workflow',
            blocksRun: true,
        });
    }
    else {
        if (workflow && !workflow.active) {
            add({
                code: 'workflow.unpublished',
                tile: 'workflow',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.execution.unpublished-workflow',
                action: 'open-builder',
                blocksRun: true,
            });
        }
        if (input.missingAiKeys.length > 0) {
            add({
                code: 'workflow.ai-keys-missing',
                tile: 'workflow',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.process.missing-ai-keys',
                action: 'open-ai-settings',
                blocksRun: false,
            });
        }
        if (input.safety?.severity === 'blocking') {
            add({
                code: 'workflow.safety-blocked',
                tile: 'workflow',
                severity: 'error',
                labelKey: input.safety.titleKey,
                action: 'review-safety',
                blocksRun: false,
            });
        }
        if (!(0, field_types_1.hasValidRelationInputMappings)(config.inputMappings, config.inputTables, schema
            ? [
                ...(schema.input?.required ?? []),
                ...(schema.input?.optional ?? []),
            ]
            : undefined)) {
            add({
                code: 'workflow.relation-mapping-invalid',
                tile: 'mapping',
                severity: 'missing',
                labelKey: 'com.streamlain.automation.sidebar.relation-mapping-invalid',
                action: 'map-input',
                blocksRun: true,
            });
        }
        if (!hasSource &&
            (config.inputMappings ?? []).some(mapping => mapping.columnId && (0, field_types_1.isOptionsInputMapping)(mapping))) {
            add({
                code: 'workflow.options-mapping-invalid',
                tile: 'mapping',
                severity: 'error',
                labelKey: 'com.streamlain.automation.sidebar.options-mapping-invalid',
                action: 'map-input',
                blocksRun: true,
            });
        }
        // A row-list input receives the rows of the run; no column fills it.
        const required = (schema?.input?.required ?? []).filter(field => (0, field_types_1.normalizeType)(field.type ?? '') !== field_types_1.ROW_LIST_FIELD_TYPE);
        const mapped = new Set((config.inputMappings ?? [])
            .filter(mapping => mapping.schemaFieldId && mapping.columnId)
            .map(mapping => mapping.schemaFieldId));
        if (required.some(field => !mapped.has(field.id))) {
            add({
                code: 'workflow.input-mapping-missing',
                tile: 'mapping',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.execution.missing-input-mapping',
                action: 'map-input',
                blocksRun: true,
            });
        }
    }
    // ── Ausgabe ──
    const outputTables = config.outputTables ?? [];
    const targets = outputTables.filter(table => table.tableId);
    // The output is optional: an automation without entries writes nothing
    // back. Only an entry that names no table is open.
    if (targets.length !== outputTables.length) {
        add({
            code: 'output.target-missing',
            tile: 'target',
            severity: 'missing',
            labelKey: 'com.streamlain.streamlain.execution.missing-output-table',
            action: 'select-target',
            blocksRun: true,
        });
    }
    if (config.workflowId && targets.length > 0) {
        const mappedFields = new Set((config.outputMappings ?? [])
            .map(mapping => mapping.responseField)
            .filter(Boolean));
        const requiredOutputs = Object.entries(schema?.output ?? {}).filter(([, field]) => field.required);
        if (requiredOutputs.some(([id]) => !mappedFields.has(id))) {
            add({
                code: 'output.mapping-missing',
                tile: 'fields',
                severity: 'missing',
                labelKey: 'com.streamlain.streamlain.execution.missing-output-mapping',
                action: 'map-output',
                blocksRun: true,
            });
        }
    }
    const sourceAsTarget = hasSource
        ? targets.find(table => table.tableId === config.sourceTableId)
        : undefined;
    if (sourceAsTarget &&
        !updatesSourceRow(config, sourceAsTarget) &&
        (0, trigger_1.isActivatableTrigger)(trigger)) {
        const matchConfig = sourceAsTarget.matchConfigId
            ? (config.matchConfigs ?? []).find(entry => entry.id === sourceAsTarget.matchConfigId)
            : undefined;
        if (!matchConfig || matchConfig.keys.length === 0) {
            add({
                code: 'output.match-key-missing',
                tile: 'match',
                severity: 'missing',
                labelKey: 'com.streamlain.automation.sidebar.match-missing',
                action: 'set-match-key',
                blocksRun: false,
            });
        }
    }
    // ── Verlauf ──
    if (input.lastRun?.status === 'failed' ||
        LOOP_REASONS.has(input.lastRun?.terminalReason ?? '')) {
        add({
            code: 'history.last-run-failed',
            tile: 'history',
            severity: 'error',
            labelKey: 'com.streamlain.automation.sidebar.last-run-failed',
            action: 'open-history',
            blocksRun: false,
        });
    }
    const issuesOf = (tile) => issues.filter(issue => issue.tile === tile);
    const filterCount = config.filterGroup?.conditions?.length ?? 0;
    const hasMatch = targets.some(table => !!table.matchConfigId || updatesSourceRow(config, table)) || (config.outputMappings ?? []).some(mapping => !!mapping.matchConfigId);
    const tiles = {
        source: stateOf(issuesOf('source'), 'ok'),
        related: stateOf(issuesOf('related'), hasSource && secondaries.length > 0 ? 'ok' : 'none'),
        // Marking rows adds a filter that skips rows already marked done.
        filter: hasSource &&
            (filterCount > 0 || (trackingComplete && tracking?.mode !== 'delete_row'))
            ? 'ok'
            : 'none',
        tracking: stateOf(issuesOf('tracking'), trackingComplete ? 'ok' : 'none'),
        rows: hasSource ? 'ok' : 'none',
        workflow: stateOf(issuesOf('workflow'), 'ok'),
        // A workflow with input fields gets its mapping as soon as it is
        // selected, required or not: the fields are what the rows hand over.
        mapping: stateOf(issuesOf('mapping'), config.workflowId &&
            ((config.inputMappings ?? []).some(mapping => mapping.columnId) ||
                (schema?.input?.required?.length ?? 0) > 0 ||
                (schema?.input?.optional?.length ?? 0) > 0)
            ? 'ok'
            : 'none'),
        test: config.workflowId && input.hasTestContext ? 'ok' : 'none',
        failure: config.workflowId ? 'ok' : 'none',
        target: stateOf(issuesOf('target'), targets.length > 0 ? 'ok' : 'none'),
        fields: stateOf(issuesOf('fields'), (config.outputMappings ?? []).length > 0 ? 'ok' : 'none'),
        match: stateOf(issuesOf('match'), hasMatch ? 'ok' : 'none'),
        fanout: targets.some(table => !!table.sourceArrayField) ? 'ok' : 'none',
        // A webhook without credential / a row trigger without source table
        // cannot fire; the gap itself is listed on the source tile.
        trigger: (isWebhook || (0, trigger_1.isRowTrigger)(trigger)) && !inputConfigured
            ? 'missing'
            : 'ok',
        history: stateOf(issuesOf('history'), input.lastRun?.status === 'completed' ? 'ok' : 'none'),
    };
    const steps = Object.fromEntries(exports.READINESS_STEPS.map(step => {
        const stepIssues = issues.filter(issue => readinessGroupOf(issue.tile) === step);
        return [step, { state: stateOf(stepIssues, 'ok'), issues: stepIssues }];
    }));
    const groups = Object.fromEntries(exports.READINESS_GROUPS.map(group => [
        group.id,
        {
            state: group.tiles.reduce((worst, tile) => STATE_RANK[tiles[tile]] > STATE_RANK[worst] ? tiles[tile] : worst, 'none'),
            issues: issues.filter(issue => group.tiles.includes(issue.tile)),
        },
    ]));
    const missing = issues.filter(issue => issue.severity === 'missing');
    const canRun = !missing.some(issue => issue.blocksRun);
    const canActivate = (0, trigger_1.isActivatableTrigger)(trigger) &&
        missing.length === 0 &&
        input.safety?.severity !== 'blocking';
    const active = (0, trigger_1.isActivatableTrigger)(trigger) && !!config.triggerConfig?.enabled;
    const ready = (0, trigger_1.isActivatableTrigger)(trigger) ? canActivate : canRun;
    return {
        tiles,
        issues,
        steps,
        groups,
        canRun,
        canActivate,
        status: active ? 'active' : ready ? 'ready' : 'setup',
        openCount: missing.length,
    };
}
//# sourceMappingURL=readiness.js.map